import apiClient from "./middleware";

export type DiscrepancySeverity = "blocker" | "validation" | "hygiene";

export interface MetadataAuditComparerRun {
  runId: string;
  dateUploaded: string;
  dateCompleted?: string;
  status: "pending" | "running" | "completed" | "failed";
  fingerprint: string;
  fileName: string;
  rowCounts?: {
    totalRows: number;
    discrepancyRows: number;
    manualReviewRows: number;
  };
  percentages?: {
    discrepancyPct: number;
    manualReviewPct: number;
  };
  errorMessage?: string;
}

export interface MetadataAuditComparerRow {
  runId: string;
  rowId: string;
  rowIndex: number;
  assetKey: string;
  hasDiscrepancy: boolean;
  processedAt: string;

  Brand: string;
  CustomId: string;
  SRAssetId: string;
  UMPGInternalWorkCode: string;
  MCTitle: string;
  MCISWC: string;
  MCWriters: string;
  CompTitle: string;
  CompISWC: string;
  CompWriter: string;
  CompAssetId: string;
  SRTitle: string;
  SRISRC: string;
  SRArtist: string;
  IdentifiedIssues: string[];
  ManualReviewRequired: boolean;
  MatchConfidence: number;
  DiscrepancyTypes: string;
  DiscrepancySeverity: DiscrepancySeverity | "";
  DataHygieneAlert: boolean;
  HygieneTypes: string;
}

export interface MetadataAuditComparerMetrics {
  totalRows: number;
  discrepancyRows: number;
  manualReviewRows: number;
  discrepancyPct: number;
  manualReviewPct: number;
}

export const metadataAuditComparerApi = {
  listRuns: async (
    limit = 50,
  ): Promise<{ runs: MetadataAuditComparerRun[] }> => {
    const { data } = await apiClient.get("/metadata-audit-comparer/runs", {
      params: { limit },
    });
    return data;
  },

  getRun: async (
    runId: string,
  ): Promise<{
    run: MetadataAuditComparerRun;
    metrics: MetadataAuditComparerMetrics | null;
  }> => {
    const { data } = await apiClient.get(
      `/metadata-audit-comparer/runs/${runId}`,
    );
    return data;
  },

  getRunRows: async (
    runId: string,
    params: {
      limit?: number;
      startAfterRowIndex?: number;
      onlyDiscrepancy?: boolean;
    } = {},
  ): Promise<{
    rows: MetadataAuditComparerRow[];
    total: number;
    limit: number;
    nextStartAfterRowIndex?: number;
  }> => {
    const { data } = await apiClient.get(
      `/metadata-audit-comparer/runs/${runId}/rows`,
      {
        params: {
          limit: params.limit ?? 50,
          startAfterRowIndex: params.startAfterRowIndex,
          onlyDiscrepancy:
            params.onlyDiscrepancy !== undefined
              ? String(params.onlyDiscrepancy)
              : undefined,
        },
      },
    );
    return data;
  },

  listLatestDiscrepancies: async (
    params: {
      limit?: number;
      startAfterAssetKey?: string;
      startAfterMatchConfidence?: number;
      hasDiscrepancy?: boolean;
      manualReviewRequired?: boolean;
      dataHygieneAlert?: boolean;
      brand?: string;
      discrepancySeverity?: DiscrepancySeverity;
      minMatchConfidence?: number;
      maxMatchConfidence?: number;
      sortBy?: "assetKey" | "MatchConfidence";
      sortDir?: "asc" | "desc";
    } = {},
  ): Promise<{
    rows: MetadataAuditComparerRow[];
    limit: number;
    nextStartAfterAssetKey?: string;
    nextStartAfterMatchConfidence?: number;
    total: number;
  }> => {
    const { data } = await apiClient.get(
      "/metadata-audit-comparer/latest-discrepancies",
      {
        params: {
          limit: params.limit ?? 50,
          startAfterAssetKey: params.startAfterAssetKey || undefined,
          startAfterMatchConfidence: params.startAfterMatchConfidence,
          hasDiscrepancy:
            params.hasDiscrepancy !== undefined
              ? String(params.hasDiscrepancy)
              : undefined,
          manualReviewRequired:
            params.manualReviewRequired !== undefined
              ? String(params.manualReviewRequired)
              : undefined,
          dataHygieneAlert:
            params.dataHygieneAlert !== undefined
              ? String(params.dataHygieneAlert)
              : undefined,
          brand: params.brand || undefined,
          discrepancySeverity: params.discrepancySeverity || undefined,
          minMatchConfidence: params.minMatchConfidence,
          maxMatchConfidence: params.maxMatchConfidence,
          sortBy: params.sortBy || undefined,
          sortDir: params.sortDir || undefined,
        },
      },
    );
    return data;
  },
};
